import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
    bulkRoiSelectionActiveState,
    selectedROIState,
} from '~/state/atoms/structureSet/roiAtoms';
import { operationPanelState } from '~/state/atoms/toolsAtoms';
import UnifiedOperationPanel from './UnifiedOperationPanel';

interface OperationsPanelHandlerProps {
    toolbarRef: RefObject<HTMLElement | null>;
}

export default function OperationsPanelHandler({
    toolbarRef,
}: OperationsPanelHandlerProps) {
    const [panelState, setPanelState] = useRecoilState(operationPanelState);
    const selectedROI = useRecoilValue(selectedROIState);
    const isBulkSelectionActive = useRecoilValue(bulkRoiSelectionActiveState);
    const isOpen = panelState.type !== 'closed';

    const [position, setPosition] = useState<{
        x: number;
        y: number;
    } | null>(null);

    const lastPositionRef = useRef<{ x: number; y: number } | null>(null);

    const getDefaultPosition = useCallback(() => {
        const anchor = toolbarRef.current;
        if (!anchor) {
            return null;
        }
        const rect = anchor.getBoundingClientRect();
        return {
            x: rect.right + 26,
            y: Math.max(50, rect.top),
        };
    }, [toolbarRef]);

    // Restore saved position or compute default when opening
    const prevTypeRef = useRef(panelState.type);
    useEffect(() => {
        const wasClosed = prevTypeRef.current === 'closed';
        const isNowOpen = panelState.type !== 'closed';
        if (wasClosed && isNowOpen) {
            const saved = lastPositionRef.current;
            const isOnScreen =
                saved &&
                saved.x >= 0 &&
                saved.y >= 0 &&
                saved.x < window.innerWidth - 300 &&
                saved.y < window.innerHeight - 300;
            const pos = isOnScreen ? saved : getDefaultPosition();
            setPosition(pos);
            lastPositionRef.current = pos;
        }
        prevTypeRef.current = panelState.type;
    }, [panelState.type, getDefaultPosition]);

    // Auto-close margin/ring panel when no ROI is selected
    useEffect(() => {
        if (
            (selectedROI < 0 || isBulkSelectionActive) &&
            (panelState.type === 'margin' || panelState.type === 'ring')
        ) {
            setPanelState({ type: 'closed' });
        }
    }, [selectedROI, isBulkSelectionActive, panelState.type, setPanelState]);

    const handleClose = useCallback(() => {
        setPanelState({ type: 'closed' });
    }, [setPanelState]);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        setPosition((prev) => {
            if (!prev) {
                return prev;
            }
            const next = {
                x: prev.x + event.delta.x,
                y: prev.y + event.delta.y,
            };
            lastPositionRef.current = next;
            return next;
        });
    }, []);

    if (!isOpen) {
        return null;
    }

    return createPortal(
        <DndContext onDragEnd={handleDragEnd}>
            <UnifiedOperationPanel
                panelState={panelState}
                onClose={handleClose}
                position={position}
            />
        </DndContext>,
        document.body
    );
}
